import { Component, Input, OnInit, ViewChild, ElementRef, Output, EventEmitter, ChangeDetectionStrategy } from "@angular/core";
import { BaseComponent, UserService, EventBusService, StorageKeys, StorageService, ProductService, ContentService } from "@mypxplat/xplat/core";
import { AppService } from "@mypxplat/xplat/web/core";
import { takeUntil } from "rxjs/operators";

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: "fui-content-filter",
  templateUrl: "content-filter.component.html",
  standalone: false,
})
export class FuiContentFilterComponent extends BaseComponent implements OnInit {
  @Input() resultsCount: number = 0;
  @Input() showViewOption: boolean = true;
  @Input() showResults: boolean = true;
  @Input() showSortOptionsToggle: boolean = true;
  @Input() showSidebarToggle: boolean = true;
  @Input() searchPlaceholder: string = "my_gear.search";
  @Input() showSearchInput: boolean = true;
  @Input() searchOnlyOnEnter: boolean = false;
  @Input() showRefreshButton: boolean = false;
  @Input() refreshing: boolean = false;
  @Input() activeQuery: string = "";
  @Input() filters: any;
  @Input() widerSearch: boolean = false;
  @Output() queryChanged: EventEmitter<string> = new EventEmitter();
  @Output() refineClicked: EventEmitter<void> = new EventEmitter();
  @Output() showMobileFiltersClicked: EventEmitter<void> = new EventEmitter();
  @Output() clearQuery: EventEmitter<void> = new EventEmitter();
  @Output() clearFilter: EventEmitter<string> = new EventEmitter();
  @Output() sortChanged: EventEmitter<any> = new EventEmitter();
  @Output() refreshClicked: EventEmitter<void> = new EventEmitter();
  public refining: boolean = true;
  public showSortOptions: boolean = false;
  public isSmallViewport: boolean = false;

  public settings: any = {
    sort_by: "date",
    sort_direction: "desc",
    group_by_type: true,
  };

  /**
   * i18n key for the current sort selection — bound to the sort-pill button label.
   * Kept as a plain property (not a getter/method) so template bindings don't fire it
   * every change-detection cycle. Refreshed in `ngOnInit` after `settings.sort_by` is
   * seeded from `appService`, and in `sortBy()` when the user picks a new option.
   */
  public currentSortLabelKey: string = "my_gear.date";

  constructor(
    userService: UserService,
    public appService: AppService,
    public storageService: StorageService,
    public productService: ProductService,
    public contentService: ContentService
  ) {
    super(userService);
  }

  ngOnInit(): void {
    this.settings.sort_by = this.appService.sortBy;
    this.settings.sort_direction = this.appService.sortDirection;
    this.settings.group_by_type = this.appService.sortGroupByType;
    this.isSmallViewport = this.appService.isSmallViewport;
    this.updateSortLabelKey();
    this.appService.viewportChanged$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.isSmallViewport = this.appService.isSmallViewport;
    });
  }

  /**
   * Recompute `currentSortLabelKey` from `settings.sort_by`. Called after either the
   * initial hydration or a user-driven change so the pill label stays in sync without
   * a getter/method in the template binding.
   */
  private updateSortLabelKey(): void {
    this.currentSortLabelKey = this.settings.sort_by === "abc" ? "my_gear.title" : "my_gear.date";
  }

  sortBy(type) {
    this.appService.sortBy = type;
    this.settings.sort_by = type;
    this.updateSortLabelKey();
    this.sortChanged.next({
      sort_by: type,
      sort_direction: this.settings.sort_direction,
    });
  }

  queryChangedEvent(event: any) {
    if (!this.searchOnlyOnEnter) {
      this.queryChanged.next(event);
    }
  }

  showMobileFilters() {
    if (!this.refining) {
      this.refine();
    }
    this.showMobileFiltersClicked.next();
  }

  sortDirection(direction) {
    this.appService.sortDirection = direction;
    this.settings.sort_direction = direction;
    this.sortChanged.next({
      sort_by: this.settings.sort_by,
      sort_direction: direction,
    });
  }

  refine() {
    this.refining = !this.refining;
    this.refineClicked.next();
  }

  refresh() {
    this.refreshClicked.next();
  }
}
